/**
 * LOW-07: Join-Waitlist Flow
 *
 * Handles waitlist join functionality:
 * - Toggle button for waitlist form
 * - Load estimated wait time + queue size
 * - Submit join request
 * - Display feedback
 */

import { logger } from './logger.js';
import { fetchJson } from '../../http/utils.js';
import { escapeHtml } from './sanitize.js';
import { getParamOrHidden, createFocusTrap } from './utils.js';

// ============================================
// Bot Protection (Waitlist Forms)
// ============================================
const BOT_PROTECTION_CACHE = {};
const BOT_PROTECTION_SCRIPTS = {};
const BOT_PROTECTION_SURFACE = 'ticket_purchase';

function getInviteContext(form = null) {
  return {
    inviteToken: getParamOrHidden(form, ['invite', 'invite_token']),
    inviteCode: getParamOrHidden(form, 'invite_code'),
  };
}

function getTicketAccessContext(form = null) {
  const inviteContext = getInviteContext(form);
  return {
    ...inviteContext,
    accessCode: getParamOrHidden(form, ['access_code', 'code']),
  };
}

function getBotProtectionCacheKey(eventName, { inviteToken = '', inviteCode = '' } = {}) {
  return [eventName || '', inviteToken || '', inviteCode || ''].join('::');
}

function clearBotProtectionCache(eventName, inviteContext = {}) {
  const cacheKey = getBotProtectionCacheKey(eventName, inviteContext);
  BOT_PROTECTION_CACHE[cacheKey] = null;
}

function loadBotScript(src, id) {
  if (id && document.getElementById(id)) return Promise.resolve();
  if (BOT_PROTECTION_SCRIPTS[src]) return BOT_PROTECTION_SCRIPTS[src];

  BOT_PROTECTION_SCRIPTS[src] = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    if (id) script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('script_load_failed'));
    document.head.appendChild(script);
  });

  return BOT_PROTECTION_SCRIPTS[src];
}

async function getBotProtectionConfig(eventName, inviteContext = {}) {
  if (!eventName) return { enabled: false, mode: 'none' };
  const cacheKey = getBotProtectionCacheKey(eventName, inviteContext);
  if (BOT_PROTECTION_CACHE[cacheKey]) return BOT_PROTECTION_CACHE[cacheKey];

  try {
    const { inviteToken, inviteCode } = inviteContext;
    const { payload } = await fetchJson(
      '/api/method/cita_events.api.event.event_public_api.get_bot_protection_config',
      {
        method: 'POST',
        body: {
          event: eventName,
          invite_token: inviteToken || undefined,
          invite_code: inviteCode || undefined,
          surface: BOT_PROTECTION_SURFACE,
        },
      }
    );
    const cfg = payload.data || { enabled: false, mode: 'none' };
    BOT_PROTECTION_CACHE[cacheKey] = cfg;
    return cfg;
  } catch (e) {
    return { enabled: false, mode: 'none' };
  }
}

async function injectBotProtection(form, cfg, { force = false } = {}) {
  if (!cfg?.enabled || cfg.mode === 'none') return;
  if (!force && form.dataset.botProtectionInit === '1') return;
  const existing = form.querySelector('[data-bot-protection="1"]');
  if (existing) existing.remove();
  form.dataset.botProtectionInit = '1';

  const submitBtn = form.querySelector('button[type="submit"]');
  const container = document.createElement('div');
  container.className = 'checkout-info-box checkout-mt-md';
  container.dataset.botProtection = '1';

  if (cfg.error_code) {
    container.innerHTML = `
      <label class="checkout-label">${__('Bot-Schutz *')}</label>
      <p class="checkout-helper-text checkout-text-danger">${escapeHtml(cfg.message || __('Bot-Schutz ist fehlerhaft konfiguriert.'))}</p>
    `;
  } else if (cfg.mode === 'simple') {
    container.innerHTML = `
      <label class="checkout-label">${__('Sicherheitsfrage *')}</label>
      <p class="checkout-helper-text">${escapeHtml(cfg.challenge?.question || __('Bitte beantworte die Sicherheitsfrage.'))}</p>
      <input type="text" name="bot_challenge_response" class="checkout-input" placeholder="${__('Antwort eingeben')}" autocomplete="off" required>
    `;
  } else {
    container.innerHTML = `
      <label class="checkout-label">${__('Bot-Schutz *')}</label>
      <div class="bot-protection-widget"></div>
      <input type="hidden" name="bot_challenge_response" />
    `;
    const widget = container.querySelector('.bot-protection-widget');
    const input = container.querySelector('input[name="bot_challenge_response"]');

    if (!cfg.site_key) {
      widget.textContent = __('Captcha ist nicht konfiguriert.');
    } else if (cfg.mode === 'turnstile') {
      await loadBotScript('https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit', 'turnstile-api');
      if (window.turnstile) {
        window.turnstile.render(widget, {
          sitekey: cfg.site_key,
          callback: (token) => { input.value = token || ''; },
          'expired-callback': () => { input.value = ''; },
        });
      }
    } else {
      widget.textContent = __('Dieser Bot-Schutz wird auf der Ticketkauf-Seite nicht unterstützt.');
    }
  }

  if (submitBtn && submitBtn.parentNode) {
    submitBtn.parentNode.insertBefore(container, submitBtn);
  } else {
    form.appendChild(container);
  }
}

function findWaitlistFormForButton(button) {
  const formId = button?.dataset?.waitlistFormId || '';
  if (!formId) {
    return null;
  }

  const form = document.getElementById(formId);
  if (form?.classList?.contains('waitlist-join-form')) {
    return form;
  }

  return null;
}

function findWaitlistToggleButtonForForm(form) {
  const toggleId = form?.dataset?.waitlistToggleId || '';
  if (!toggleId) {
    return null;
  }

  const button = document.getElementById(toggleId);
  if (button?.classList?.contains('toggle-waitlist-form')) {
    return button;
  }

  return null;
}

async function resolveBotProtectionResponse(cfg, form) {
  if (!cfg?.enabled || cfg.mode === 'none') return null;
  if (cfg.error_code) return null;

  if (cfg.mode === 'simple') {
    const input = form.querySelector('input[name="bot_challenge_response"]');
    const answer = input?.value?.trim();
    if (!answer) return null;
    // S11-003 Roundtrip-Vollfix (2026-06-03): muss den {answer, challenge_id}-
    // Envelope senden — identisch zu bot-protection.js. Der Backend-Write keyt
    // auf einer per-Challenge-UUID (cfg.challenge.challenge_id); ohne sie faellt
    // der Validator auf einen falschen Key zurueck → Waitlist-Join schlaegt fehl.
    const challengeId = cfg.challenge?.challenge_id;
    return challengeId
      ? JSON.stringify({ answer, challenge_id: challengeId })
      : answer;
  }

  const input = form.querySelector('input[name="bot_challenge_response"]');
  return input?.value || null;
}

/**
 * Parse Frappe _server_messages format (triple-nested JSON)
 */
function parseFrappeServerMessages(serverMessages) {
  if (!serverMessages) return null;
  const parseMaybeJson = (value) => {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const extractMessage = (value) => {
    const parsed = parseMaybeJson(value);
    if (!parsed) return null;

    if (typeof parsed === 'string') {
      const trimmed = parsed.trim();
      if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('[')) return null;
      return trimmed;
    }
    if (Array.isArray(parsed)) {
      for (const entry of parsed) {
        const nested = extractMessage(entry);
        if (nested) return nested;
      }
      return null;
    }
    if (typeof parsed === 'object') {
      if (typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message.trim();
      if (typeof parsed.toast === 'string' && parsed.toast.trim()) return parsed.toast.trim();
    }
    return null;
  };

  try {
    const parsed = extractMessage(serverMessages);
    if (parsed) return parsed;
  } catch (e) {
    if (typeof serverMessages === 'string' && serverMessages.length < 500) {
      return serverMessages.trim() || null;
    }
  }
  return null;
}

/**
 * Extract user-friendly error message from API response
 *
 * Priority: toast > _server_messages > fallback
 */
function extractWaitlistErrorMessage(response, fallback = __('Ein Fehler ist aufgetreten.')) {
  if (!response) return fallback;

  // 1. toast field (canonical API schema: json_ok/json_err)
  if (typeof response.toast === 'string' && response.toast.trim()) {
    return response.toast;
  }

  // 2. _server_messages (Frappe exceptions via frappe.throw())
  const candidates = [response, response.responseJSON, response.data].filter(Boolean);
  for (const candidate of candidates) {
    if (candidate?._server_messages) {
      const parsed = parseFrappeServerMessages(candidate._server_messages);
      if (parsed) return parsed;
    }
  }

  return fallback;
}

/**
 * Fetch estimated wait time and queue size for a ticket
 */
async function fetchWaitlistEstimate(event, ticketType, form = null) {
  const accessContext = getTicketAccessContext(form);
  try {
    const { payload } = await fetchJson(
      '/api/method/cita_events.api.event.event_public_api.get_waitlist_estimate',
      {
        method: 'POST',
        body: {
          event: event,
          ticket_type: ticketType,
          invite_token: accessContext.inviteToken || undefined,
          invite_code: accessContext.inviteCode || undefined,
          access_code: accessContext.accessCode || undefined,
        },
      }
    );

    if (payload.success) {
      return {
        queueSize: payload.data?.queue_size,
        estimatedWait: payload.data?.estimated_wait_time || null
      };
    }
  } catch (error) {
    logger.error('Failed to fetch waitlist estimate:', error);
  }
  return { queueSize: null, estimatedWait: null };
}

/**
 * Update UI with waitlist estimates
 */
function updateWaitlistUI(form, estimates) {
  const queueSizeEl = form.querySelector('.waitlist-queue-size');
  const estimatedTimeEl = form.querySelector('.waitlist-estimated-time');
  const hasQueueSize = typeof estimates?.queueSize === 'number' && Number.isFinite(estimates.queueSize);

  if (queueSizeEl) {
    queueSizeEl.textContent = hasQueueSize
      ? `${estimates.queueSize}`
      : '–';
  }

  if (estimatedTimeEl) {
    if (estimates.estimatedWait && estimates.estimatedWait.human_readable) {
      estimatedTimeEl.textContent = estimates.estimatedWait.human_readable;
    } else {
      estimatedTimeEl.textContent = __('derzeit keine Schätzung verfügbar');
    }
  }
}

/**
 * WELLE 2 Folge (OD-1, Option B) — SSOT (FW-A1, 2026-06-01): die EINE persistente
 * Inline-Bestätigungs-Karte für ALLE Waitlist-Terminals (Auto-Convert eingeloggt/Gast
 * + dedizierte Warteliste-Anmeldung via handleJoinSuccess). Vorher war diese Karte
 * byte-identisch in ticket-/guest-registration.js dupliziert. Zeigt die Position,
 * wenn die API sie liefert, sonst generisch. XSS-sicher: nur statisches SVG via
 * innerHTML + i18n-Text + validierte Integers (textContent). Wiederverwendet die
 * .checkout-info-banner-Styles. Idempotent (keine Doppel-Karte).
 * @param {HTMLElement} formEl
 * @param {{position?: number, total?: number}} [info]
 * @returns {HTMLElement|null} die eingefügte (oder bereits vorhandene) Karte, null bei Skip
 */
export function showWaitlistConfirmation(formEl, info) {
  if (!formEl) return null;
  // Idempotenz-Guard: keine Doppel-Karte, falls der Handler je erneut liefe
  // (Double-Submit-Guard + verstecktes Form verhindern Re-Entry bereits).
  const existingCard = formEl.nextElementSibling;
  if (existingCard && existingCard.classList && existingCard.classList.contains('waitlist-confirmation-card')) {
    return existingCard;
  }
  const opts = info || {};
  const pos = Number(opts.position);
  const total = Number(opts.total);
  const hasPos = Number.isFinite(pos) && pos > 0;
  const hasTotal = Number.isFinite(total) && total > 0;

  const card = document.createElement('div');
  card.className = 'checkout-info-banner checkout-mt-md waitlist-confirmation-card';
  card.setAttribute('role', 'status');
  card.setAttribute('aria-live', 'polite');

  const icon = document.createElement('div');
  icon.className = 'checkout-info-banner-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = '<svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l2 2"/></svg>';
  card.appendChild(icon);

  const content = document.createElement('div');
  content.className = 'checkout-info-banner-content';

  const title = document.createElement('h4');
  title.className = 'checkout-info-banner-title';
  title.textContent = __('Du stehst auf der Warteliste');
  content.appendChild(title);

  const details = document.createElement('div');
  details.className = 'checkout-info-banner-details';

  if (hasPos) {
    const row = document.createElement('div');
    row.className = 'checkout-info-banner-row';
    const span = document.createElement('span');
    span.textContent = hasTotal
      ? __('Deine Position: #{0} von {1}', [pos, total])
      : __('Deine Position: #{0}', [pos]);
    row.appendChild(span);
    details.appendChild(row);
  }

  const note = document.createElement('p');
  note.className = 'checkout-info-banner-note';
  note.textContent = __('Bei Verfügbarkeit erhältst du eine E-Mail mit einem exklusiven Buchungslink. Du kannst diese Seite jetzt schließen.');
  details.appendChild(note);

  content.appendChild(details);
  card.appendChild(content);

  formEl.insertAdjacentElement('afterend', card);
  formEl.classList.add('hidden');
  return card;
}

/**
 * Handle successful join — render the shared persistent confirmation card (FW-A1
 * SSOT, identischer Look wie die Auto-Convert-Terminals) und ERHALTE die dedizierte
 * Variante: Position (in der Karte), Realtime-Subscribe und Focus-Trap-Teardown.
 */
function handleJoinSuccess(response, form) {
  const toggleButton = findWaitlistToggleButtonForForm(form);
  // Position: bevorzugt die strukturierte waitlist.position, sonst legacy response.position.
  // `||` (nicht `??`) — identisch unter dem `> 0`-Gate (eine Queue-Position ist nie 0) und
  // matcht den Original-Operator; vermeidet ES2020-`??` im Source (npm-Build-Pfad ohne --target).
  const rawPosition = response.waitlist?.position || response.position;
  const position = (typeof rawPosition === 'number' && Number.isFinite(rawPosition) && rawPosition > 0)
    ? rawPosition
    : undefined;
  const total = response.waitlist?.total;
  const registration = response.registration;

  // 1. Toggle-Button → bestätigt/disabled (verhindert Wieder-Öffnen des Formulars).
  if (toggleButton) {
    toggleButton.innerHTML = `
      <svg class="checkout-icon-sm animate-checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      <span>${position ? __('Auf Warteliste (Position #{0})', [position]) : __('Auf Warteliste gesetzt')}</span>
    `;
    toggleButton.disabled = true;
    toggleButton.classList.add('waitlist-confirmed');
    toggleButton.setAttribute('aria-expanded', 'false');
  }

  // 2. Focus-Trap des Formulars abbauen (Formular wird entlassen) — kein Leak.
  if (form._waitlistFocusHandler) {
    document.removeEventListener('keydown', form._waitlistFocusHandler);
    form._waitlistFocusHandler = null;
  }
  if (form._collapseTimeout) {
    clearTimeout(form._collapseTimeout);
    form._collapseTimeout = null;
  }

  // 3. Die geteilte persistente Bestätigungs-Karte rendern (SSOT) — versteckt das
  //    Formular und fügt die Karte als Geschwister ein.
  showWaitlistConfirmation(form, { position, total });

  // 4. Realtime-Subscribe falls verfügbar (ERHALTEN — keine Regression).
  if (window.waitlistRealtime && registration) {
    try {
      window.waitlistRealtime.subscribe(registration);
    } catch (e) {
      logger.warn('Failed to subscribe to waitlist realtime:', e);
    }
  }
}

/**
 * Handle join errors with specific error codes (HIGH FIX H2)
 */
function handleJoinError(response, feedbackBox, submitBtn) {
  const errorMap = {
    'invalid_email': __('Bitte gib eine gültige E-Mail-Adresse ein.'),
    'already_on_waitlist': __('Du bist bereits auf der Warteliste.'),
    'already_registered': __('Du bist bereits für dieses Ticket registriert.'),
    'consent_dsgvo': __('Bitte akzeptiere die Datenschutzerklärung.'),
    'consent_agb': __('Bitte akzeptiere die AGB.'),
    'consent_required': __('Bitte akzeptiere die Datenschutzerklärung und AGB.'),
    'organizer_terms_required': __('Bitte akzeptiere die Veranstalter-AGB.'),
    'group_too_large': __('Maximale Gruppengröße überschritten.'),
    'group_exceeds_capacity': __('Gruppengröße überschreitet Ticket-Kapazität.'),
    'ticket_not_found': __('Dieses Ticket existiert nicht mehr.'),
    'waitlist_disabled': __('Warteliste ist für dieses Ticket nicht aktiviert.'),
    'not_sold_out': __('Es sind noch Plätze verfügbar. Bitte normal registrieren.'),
    'bot_challenge_failed': __('Bot-Verifizierung fehlgeschlagen. Bitte versuche es erneut.'),
    'bot_challenge_required': __('Bitte die Bot-Verifizierung abschließen.'),
    'lock_timeout': __('Server überlastet. Bitte versuche es in wenigen Sekunden erneut.'),
    'membership_required': __('Bitte gib deine Zusatzangabe an.'),
    'membership_format_invalid': __('Das Format der Zusatzangabe ist ungültig.'),
    'membership_regex_mismatch': __('Die Zusatzangabe entspricht nicht dem erforderlichen Muster.'),
    'membership_not_allowed': __('Diese Zusatzangabe ist für das Ticket nicht berechtigt.'),
    'membership_already_used': __('Diese Zusatzangabe wurde bereits verwendet.'),
    'membership_too_long': __('Die Zusatzangabe ist zu lang.'),
    'membership_config_invalid': __('Die Membership-Konfiguration ist unvollständig.'),
    'membership_invalid': __('Ungültige Zusatzangabe.'),
    'account_lookup_failed': __('Konto-Prüfung fehlgeschlagen. Bitte versuche es in wenigen Minuten erneut.'),
    'account_check': __('Registrierung konnte nicht abgeschlossen werden. Bitte einloggen oder eine andere E-Mail verwenden.'),
    'account_required': __('Registrierung konnte nicht abgeschlossen werden. Bitte einloggen oder eine andere E-Mail verwenden.'),
    'account_exists': __('Ein Account mit dieser E-Mail existiert bereits. Bitte melde dich an.'),
    'age_check_failed': __('Altersanforderung nicht erfüllt.'),
    'age_confirmation_required': __('Bitte bestätige dein Mindestalter.'),
    'birth_date_required': __('Geburtsdatum ist erforderlich für dieses Ticket.'),
    'too_young': __('Du erfüllst die Altersanforderung für dieses Ticket nicht.'),
    'invalid_birth_date': __('Ungültiges Geburtsdatum.'),
    'invalid_birth_date_future': __('Geburtsdatum darf nicht in der Zukunft liegen.'),
    'plus_one_group_blocked': __('Gruppenbuchungen können keine Begleitpersonen haben.'),
  };

  const errorCode = response.code || 'unknown';
  // Use error map first, then try to extract from _server_messages, then fallback
  const message = errorMap[errorCode] || extractWaitlistErrorMessage(response, __('Ein Fehler ist aufgetreten.'));

  showFeedback(feedbackBox, message, 'error');

  // Specific highlighting for consent errors
  if (errorCode === 'consent_dsgvo' || errorCode === 'consent_agb' || errorCode === 'consent_required' || errorCode === 'organizer_terms_required') {
    const form = feedbackBox.closest('form');
    if (form) {
      form.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        if (!cb.checked) {
          const label = cb.closest('label') || cb.parentElement;
          if (label) {
            label.classList.add('highlight-error');
            setTimeout(() => label.classList.remove('highlight-error'), 3000);
          }
        }
      });
    }
  }

  // Re-enable submit
  if (submitBtn) {
    submitBtn.disabled = false;
    const span = submitBtn.querySelector('span');
    if (span) {
      span.textContent = __('Jetzt auf Warteliste setzen');
    }
  }
}

function initWaitlistMembershipToggle(root) {
  root.querySelectorAll('.waitlist-join-form').forEach((form) => {
    const askMembership = form.dataset.askMembership === '1' || form.dataset.askMembership === 'true';
    const required = form.dataset.membershipRequired === '1' || form.dataset.membershipRequired === 'true';
    const checkbox = form.querySelector('input[name="has_membership_number"]');
    const wrapper = form.querySelector('.waitlist-membership-wrap, [data-waitlist-membership-wrapper]');
    const input = wrapper ? wrapper.querySelector('input[name="membership_number"]') : null;

    if (!askMembership || !wrapper || !input) {
      return;
    }

    const applyVisibility = () => {
      const showInput = required || (checkbox ? checkbox.checked : false);
      wrapper.classList.toggle('hidden', !showInput);
      input.required = required;
      if (!showInput && !required) {
        input.value = '';
      }
    };

    if (required) {
      if (checkbox) {
        checkbox.checked = true;
        checkbox.disabled = true;
        checkbox.setAttribute('aria-disabled', 'true');
      }
      form.querySelectorAll('.membership-optional').forEach((el) => el.classList.add('hidden'));
    }

    if (checkbox && checkbox.dataset.waitlistMembershipInit !== '1') {
      checkbox.dataset.waitlistMembershipInit = '1';
      checkbox.addEventListener('change', applyVisibility);
    }

    applyVisibility();
  });
}

/**
 * Handle waitlist form submission (logged-in users)
 */
async function handleWaitlistSubmit(event, form) {
  event.preventDefault();

  // Double-submission guard (consistent with guest-registration.js)
  if (form.dataset.submitting === '1') return;
  form.dataset.submitting = '1';

  const ticketId = form.dataset.ticket;
  const eventId = form.dataset.event;
  const submitBtn = form.querySelector('button[type="submit"]');
  const feedbackBox = form.querySelector('.waitlist-feedback');

  // Validate consents
  const consentDsgvo = form.querySelector('input[name="consent_dsgvo"]');
  const consentAgb = form.querySelector('input[name="consent_agb"]');

  if (!consentDsgvo?.checked || !consentAgb?.checked) {
    showFeedback(feedbackBox, __('Bitte stimme den erforderlichen Bedingungen zu.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  // #1076 (ANN): organizer-terms consent. Hidden mirror has_organizer_terms=1/0
  // tells us whether the event has active terms (rendered by the macro). When
  // active the box is mandatory; absent box => true (no active terms), mirroring
  // guest-registration.js:189. The boolean is the only thing sent to the server.
  const hasOrganizerTerms = form.querySelector('input[name="has_organizer_terms"]')?.value === '1';
  const consentOrganizerEl = form.querySelector('input[name="consent_organizer_terms"]');
  const consentOrganizer = hasOrganizerTerms ? !!consentOrganizerEl?.checked : true;
  if (!consentOrganizer) {
    showFeedback(feedbackBox, __('Bitte akzeptiere die Veranstalter-AGB.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  // Get group size (if field exists)
  const groupSizeInput = form.querySelector('input[name="group_size"]');
  const groupSize = groupSizeInput ? parseInt(groupSizeInput.value) || 1 : 1;

  // Get access code (if present - for hidden tickets)
  const accessContext = getTicketAccessContext(form);
  const accessCode = accessContext.accessCode || null;

  const membershipRequired = form.dataset.membershipRequired === '1' || form.dataset.membershipRequired === 'true';
  const membershipInput = form.querySelector('input[name="membership_number"]');
  const hasMembershipInput = form.querySelector('input[name="has_membership_number"]');
  const membershipNumber = membershipRequired
    ? (membershipInput?.value?.trim() || '')
    : hasMembershipInput?.checked
    ? (membershipInput?.value?.trim() || '')
    : '';
  if (membershipRequired && !membershipNumber) {
    showFeedback(feedbackBox, __('Bitte gib deine Zusatzangabe an.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  // Age verification (checkbox mode only — birth_date comes from user profile for logged-in)
  const ageVerificationMode = form.dataset.ageMode || 'birth_date';
  const minAge = parseInt(form.dataset.minAge || '0', 10);
  const ageConfirmedInput = form.querySelector('input[name="age_confirmed"]');
  const ageConfirmed = (ageVerificationMode === 'checkbox' && minAge > 0) ? !!ageConfirmedInput?.checked : false;
  const inviteContext = accessContext;

  // Bot protection (if enabled)
  const botConfig = await getBotProtectionConfig(eventId, inviteContext);
  if (botConfig?.enabled && botConfig.mode !== 'none') {
    await injectBotProtection(form, botConfig);
    const botResponse = await resolveBotProtectionResponse(botConfig, form);
    if (!botResponse) {
      showFeedback(feedbackBox, __('Bitte die Bot-Verifizierung abschließen.'), 'error');
      form.dataset.submitting = '0';
      return;
    }
    form.dataset.botChallengeResponse = botResponse;
  } else {
    form.dataset.botChallengeResponse = '';
  }

  // Disable submit button
  if (submitBtn) {
    submitBtn.disabled = true;
    const span = submitBtn.querySelector('span');
    if (span) {
      span.textContent = __('Wird hinzugefügt...');
    }
  }

  try {
    // Tier auto-selection (matching pricing.js:75-84 logic)
    let tierId = undefined;
    const tiersJson = form.dataset.tiers;
    if (tiersJson) {
      try {
        const tiers = JSON.parse(tiersJson);
        if (tiers && tiers.length > 0) {
          const applicableTier = tiers
            .filter(t => groupSize >= (t.min_quantity || 1))
            .sort((a, b) => (b.min_quantity || 1) - (a.min_quantity || 1))[0];
          if (applicableTier) {
            tierId = applicableTier.name;
          }
        }
      } catch (e) { /* ignore parse errors */ }
    }

    const { payload: res } = await fetchJson(
      '/api/method/cita_events.api.event.event_user_api.join_waitlist',
      {
        method: 'POST',
        body: {
          event: eventId,
          ticket_type: ticketId,
          group_size: groupSize,
          consent_dsgvo: true,
          consent_agb: true,
          // #1076 (ANN): send only when the event has active terms (else omit;
          // the server forces False on inactive-terms events regardless).
          consent_organizer_terms: hasOrganizerTerms ? consentOrganizer : undefined,
          access_code: accessCode || undefined,
          membership_number: membershipNumber || undefined,
          age_confirmed: ageConfirmed || undefined,
          bot_challenge_response: form.dataset.botChallengeResponse || undefined,
          tier_id: tierId,
          invite_token: inviteContext.inviteToken || undefined,
          invite_code: inviteContext.inviteCode || undefined,
        },
      }
    );

    if (res.success) {
      // HIGH FIX H1: Handle success with animation (no page reload)
      handleJoinSuccess(res.data, form);
    } else {
      // HIGH FIX H2: Handle error with specific messages
      handleJoinError(res, feedbackBox, submitBtn);
      if (botConfig?.enabled && botConfig.mode === 'simple') {
        clearBotProtectionCache(eventId, inviteContext);
        form.dataset.botProtectionInit = '0';
        const refreshed = await getBotProtectionConfig(eventId, inviteContext);
        await injectBotProtection(form, refreshed, { force: true });
      }
    }

  } catch (error) {
    logger.error('Waitlist join failed:', error);
    // Try to extract meaningful error message
    const errorMsg = extractWaitlistErrorMessage(error, __('Ein Fehler ist aufgetreten. Bitte versuche es erneut.'));
    showFeedback(
      feedbackBox,
      errorMsg,
      'error'
    );

    // Re-enable submit
    if (submitBtn) {
      submitBtn.disabled = false;
      const span = submitBtn.querySelector('span');
      if (span) {
        span.textContent = __('Jetzt auf Warteliste setzen');
      }
    }

    if (botConfig?.enabled && botConfig.mode === 'simple') {
      clearBotProtectionCache(eventId, inviteContext);
      form.dataset.botProtectionInit = '0';
      const refreshed = await getBotProtectionConfig(eventId, inviteContext);
      await injectBotProtection(form, refreshed, { force: true });
    }
  } finally {
    form.dataset.submitting = '0';
  }
}

/**
 * Handle guest waitlist form submission (non-logged-in users)
 * Security: Uses dedicated guest API with rate limiting (5/min per IP)
 */
async function handleGuestWaitlistSubmit(event, form, { openInlineLogin } = {}) {
  event.preventDefault();

  // Double-submission guard (consistent with guest-registration.js)
  if (form.dataset.submitting === '1') return;
  form.dataset.submitting = '1';

  const ticketId = form.dataset.ticket;
  const eventId = form.dataset.event;
  const submitBtn = form.querySelector('button[type="submit"]');
  const feedbackBox = form.querySelector('.waitlist-feedback');

  // Get guest fields
  const firstNameInput = form.querySelector('input[name="first_name"]');
  const lastNameInput = form.querySelector('input[name="last_name"]');
  const emailInput = form.querySelector('input[name="email"]');

  const firstName = firstNameInput?.value?.trim() || '';
  const lastName = lastNameInput?.value?.trim() || '';
  const email = emailInput?.value?.trim() || '';

  // Client-side validation
  if (!firstName || !lastName) {
    showFeedback(feedbackBox, __('Bitte gib deinen Vor- und Nachnamen ein.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  if (!email) {
    showFeedback(feedbackBox, __('Bitte gib deine E-Mail-Adresse ein.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  // Basic email format validation (server does full validation)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    showFeedback(feedbackBox, __('Bitte gib eine gültige E-Mail-Adresse ein.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  // Validate consents
  const consentDsgvo = form.querySelector('input[name="consent_dsgvo"]');
  const consentAgb = form.querySelector('input[name="consent_agb"]');

  if (!consentDsgvo?.checked || !consentAgb?.checked) {
    showFeedback(feedbackBox, __('Bitte stimme den erforderlichen Bedingungen zu.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  // #1076 (ANN): organizer-terms consent (guest). Same semantics as the
  // logged-in path: active terms => box mandatory; absent => true.
  const hasOrganizerTerms = form.querySelector('input[name="has_organizer_terms"]')?.value === '1';
  const consentOrganizerEl = form.querySelector('input[name="consent_organizer_terms"]');
  const consentOrganizer = hasOrganizerTerms ? !!consentOrganizerEl?.checked : true;
  if (!consentOrganizer) {
    showFeedback(feedbackBox, __('Bitte akzeptiere die Veranstalter-AGB.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  // Get group size (if field exists)
  const groupSizeInput = form.querySelector('input[name="group_size"]');
  const groupSize = groupSizeInput ? parseInt(groupSizeInput.value) || 1 : 1;

  // Get access code (if present - for hidden tickets)
  const accessContext = getTicketAccessContext(form);
  const accessCode = accessContext.accessCode || null;

  const membershipRequired = form.dataset.membershipRequired === '1' || form.dataset.membershipRequired === 'true';
  const membershipInput = form.querySelector('input[name="membership_number"]');
  const hasMembershipInput = form.querySelector('input[name="has_membership_number"]');
  const membershipNumber = membershipRequired
    ? (membershipInput?.value?.trim() || '')
    : hasMembershipInput?.checked
    ? (membershipInput?.value?.trim() || '')
    : '';
  if (membershipRequired && !membershipNumber) {
    showFeedback(feedbackBox, __('Bitte gib deine Zusatzangabe an.'), 'error');
    form.dataset.submitting = '0';
    return;
  }

  // Age verification fields
  const ageVerificationMode = form.dataset.ageMode || 'birth_date';
  const minAge = parseInt(form.dataset.minAge || '0', 10);
  const birthDateInput = form.querySelector('input[name="birth_date"]');
  const ageConfirmedInput = form.querySelector('input[name="age_confirmed"]');
  const birthDate = (ageVerificationMode === 'birth_date' && minAge > 0) ? (birthDateInput?.value?.trim() || null) : null;
  const ageConfirmed = (ageVerificationMode === 'checkbox' && minAge > 0) ? !!ageConfirmedInput?.checked : false;
  const inviteContext = accessContext;

  // Bot protection (if enabled)
  const botConfig = await getBotProtectionConfig(eventId, inviteContext);
  if (botConfig?.enabled && botConfig.mode !== 'none') {
    await injectBotProtection(form, botConfig);
    const botResponse = await resolveBotProtectionResponse(botConfig, form);
    if (!botResponse) {
      showFeedback(feedbackBox, __('Bitte die Bot-Verifizierung abschließen.'), 'error');
      form.dataset.submitting = '0';
      return;
    }
    form.dataset.botChallengeResponse = botResponse;
  } else {
    form.dataset.botChallengeResponse = '';
  }

  // Disable submit button
  if (submitBtn) {
    submitBtn.disabled = true;
    const span = submitBtn.querySelector('span');
    if (span) {
      span.textContent = __('Wird hinzugefügt...');
    }
  }

  try {
    // Tier auto-selection (matching pricing.js:75-84 logic)
    let guestTierId = undefined;
    const guestTiersJson = form.dataset.tiers;
    if (guestTiersJson) {
      try {
        const tiers = JSON.parse(guestTiersJson);
        if (tiers && tiers.length > 0) {
          const applicableTier = tiers
            .filter(t => groupSize >= (t.min_quantity || 1))
            .sort((a, b) => (b.min_quantity || 1) - (a.min_quantity || 1))[0];
          if (applicableTier) {
            guestTierId = applicableTier.name;
          }
        }
      } catch (e) { /* ignore parse errors */ }
    }

    const { payload: res } = await fetchJson(
      '/api/method/cita_events.api.event.guest_registration_api.join_waitlist_guest',
      {
        method: 'POST',
        body: {
          first_name: firstName,
          last_name: lastName,
          email: email,
          event: eventId,
          ticket_type: ticketId,
          consent_dsgvo: true,
          consent_agb: true,
          // #1076 (ANN): only sent when the event has active terms.
          consent_organizer_terms: hasOrganizerTerms ? consentOrganizer : undefined,
          access_code: accessCode || undefined,
          group_size: groupSize,
          membership_number: membershipNumber || undefined,
          birth_date: birthDate || undefined,
          age_confirmed: ageConfirmed || undefined,
          bot_challenge_response: form.dataset.botChallengeResponse || undefined,
          tier_id: guestTierId,
          invite_token: inviteContext.inviteToken || undefined,
          invite_code: inviteContext.inviteCode || undefined,
        },
      }
    );

    if (res.success) {
      // Use same success handler - works for both logged-in and guest
      handleJoinSuccess(res.data, form);
    } else {
      // Account-check: try inline login if panel exists, otherwise show login hint
      const accountCodes = ['account_exists', 'account_check', 'account_required'];
      if (accountCodes.includes(res.code)) {
        const hasLoginPanel = !!form.querySelector('[data-login-inline]');
        if (hasLoginPanel && typeof openInlineLogin === 'function') {
          showFeedback(feedbackBox, __('Diese E-Mail ist bereits registriert. Bitte kurz einloggen – danach geht\'s automatisch weiter.'), 'error');
          openInlineLogin(form, () => {
            const provCb = form.querySelector('input[name="provision_account"]');
            if (provCb) provCb.checked = false;
            form.requestSubmit();
          });
        } else {
          // No inline login panel in waitlist forms — show message with login link
          showFeedback(feedbackBox, __('Diese E-Mail ist bereits registriert. Bitte zuerst einloggen.'), 'error');
        }
        if (submitBtn) {
          submitBtn.disabled = false;
          const span = submitBtn.querySelector('span');
          if (span) span.textContent = __('Jetzt auf Warteliste setzen');
        }
      } else {
        // Use same error handler - includes guest-specific errors
        handleJoinError(res, feedbackBox, submitBtn);
      }
      if (botConfig?.enabled && botConfig.mode === 'simple') {
        clearBotProtectionCache(eventId, inviteContext);
        form.dataset.botProtectionInit = '0';
        const refreshed = await getBotProtectionConfig(eventId, inviteContext);
        await injectBotProtection(form, refreshed, { force: true });
      }
    }

  } catch (error) {
    logger.error('Guest waitlist join failed:', error);
    const errorMsg = extractWaitlistErrorMessage(error, __('Ein Fehler ist aufgetreten. Bitte versuche es erneut.'));
    showFeedback(feedbackBox, errorMsg, 'error');

    // Re-enable submit
    if (submitBtn) {
      submitBtn.disabled = false;
      const span = submitBtn.querySelector('span');
      if (span) {
        span.textContent = __('Jetzt auf Warteliste setzen');
      }
    }

    if (botConfig?.enabled && botConfig.mode === 'simple') {
      clearBotProtectionCache(eventId, inviteContext);
      form.dataset.botProtectionInit = '0';
      const refreshed = await getBotProtectionConfig(eventId, inviteContext);
      await injectBotProtection(form, refreshed, { force: true });
    }
  } finally {
    form.dataset.submitting = '0';
  }
}

/**
 * Show feedback message in feedback box
 */
function showFeedback(feedbackBox, message, type = 'info') {
  if (!feedbackBox) return;

  feedbackBox.classList.remove('hidden',
    'waitlist-feedback--success', 'waitlist-feedback--error', 'waitlist-feedback--info');
  feedbackBox.classList.add(`waitlist-feedback--${type}`);

  feedbackBox.textContent = message;
}

/**
 * Initialize waitlist join forms (LOW-07)
 * Supports both logged-in users and guests
 */
function initWaitlistJoinForms(root, { openInlineLogin } = {}) {
  initWaitlistMembershipToggle(root);

  // Toggle buttons for waitlist forms
  root.querySelectorAll('.toggle-waitlist-form').forEach((button) => {
    if (button.dataset.waitlistToggleInit === '1') {
      return;
    }
    button.dataset.waitlistToggleInit = '1';

    button.addEventListener('click', async () => {
      const ticketId = button.dataset.ticket;
      const eventId = button.dataset.event;
      const isGuest = button.dataset.isGuest === '1';

      const form = findWaitlistFormForButton(button);

      if (!form) {
        logger.error('Waitlist form not found for ticket:', ticketId, 'isGuest:', isGuest);
        return;
      }

      // Toggle form visibility
      const isHidden = form.classList.contains('hidden');

      if (isHidden) {
        // Cancel any pending collapse timeout
        clearTimeout(form._collapseTimeout);
        form._collapseTimeout = null;

        // Store trigger for focus restoration on close
        form._waitlistTrigger = button;

        // Expand form - remove hidden first, then add expanded class
        form.classList.remove('hidden');
        button.setAttribute('aria-expanded', 'true');
        // Use requestAnimationFrame to ensure DOM has updated before adding expanded class
        requestAnimationFrame(() => {
          form.classList.add('waitlist-form-expanded');
        });

        // A11y: Focus trap — ESC closes, Tab cycles within form
        const collapseForm = () => {
          form.classList.remove('waitlist-form-expanded');
          button.setAttribute('aria-expanded', 'false');
          clearTimeout(form._collapseTimeout);
          form._collapseTimeout = setTimeout(() => {
            form.classList.add('hidden');
            form._collapseTimeout = null;
          }, 250); // Match --checkout-anim-normal
          // Remove focus trap
          if (form._waitlistFocusHandler) {
            document.removeEventListener('keydown', form._waitlistFocusHandler);
            form._waitlistFocusHandler = null;
          }
          // Restore focus to trigger button
          if (form._waitlistTrigger && typeof form._waitlistTrigger.focus === 'function') {
            try { form._waitlistTrigger.focus(); } catch (_) { /* element may be gone */ }
          }
        };

        // Clean up any previous focus trap
        if (form._waitlistFocusHandler) {
          document.removeEventListener('keydown', form._waitlistFocusHandler);
        }
        form._waitlistFocusHandler = createFocusTrap(form, {
          onEscape: collapseForm,
          initialFocus: 'input:not(:disabled)',
        });
        document.addEventListener('keydown', form._waitlistFocusHandler);

        // Load estimates
        const estimates = await fetchWaitlistEstimate(eventId, ticketId, form);
        updateWaitlistUI(form, estimates);

        // Inject bot protection if enabled
        const botCfg = await getBotProtectionConfig(eventId, getInviteContext(form));
        await injectBotProtection(form, botCfg);

        // Scroll into view after animation starts
        setTimeout(() => {
          if (typeof form.scrollIntoView === 'function') {
            form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 50);

      } else {
        // Collapse form - remove expanded class, then hide after transition
        form.classList.remove('waitlist-form-expanded');
        button.setAttribute('aria-expanded', 'false');
        // Remove focus trap
        if (form._waitlistFocusHandler) {
          document.removeEventListener('keydown', form._waitlistFocusHandler);
          form._waitlistFocusHandler = null;
        }
        // Restore focus to trigger button
        if (form._waitlistTrigger && typeof form._waitlistTrigger.focus === 'function') {
          try { form._waitlistTrigger.focus(); } catch (_) { /* element may be gone */ }
        }
        clearTimeout(form._collapseTimeout);
        form._collapseTimeout = setTimeout(() => {
          form.classList.add('hidden');
          form._collapseTimeout = null;
        }, 250); // Match --checkout-anim-normal
      }
    });
  });

  // Submit handlers for waitlist forms
  // Route to correct handler based on data-is-guest attribute
  root.querySelectorAll('.waitlist-join-form').forEach((form) => {
    if (form.dataset.waitlistSubmitInit === '1') {
      return;
    }
    form.dataset.waitlistSubmitInit = '1';

    const isGuestForm = form.dataset.isGuest === '1';

    form.addEventListener('submit', (event) => {
      if (isGuestForm) {
        handleGuestWaitlistSubmit(event, form, { openInlineLogin });
      } else {
        handleWaitlistSubmit(event, form);
      }
    });
  });
}

/**
 * A-2 / golive-audit H-13: programmatically reveal the waitlist join form for a ticket.
 *
 * The paid-checkout endpoint returns SOLD_OUT_WAITLIST_AVAILABLE (data.redirect="waitlist")
 * when a capacity-limited, waitlist-enabled ticket is sold out. Before this helper the
 * checkout handlers ignored that signal and showed a generic "Zahlung fehlgeschlagen"
 * error, dead-ending the guest. This reuses the existing toggle-button flow (estimates,
 * bot protection, focus trap, scroll) by clicking the ticket's waitlist toggle.
 *
 * @param {string} ticketType - Event Ticket docname.
 * @param {Object} [opts]
 * @param {ParentNode} [opts.root=document]
 * @returns {boolean} true if a waitlist affordance for the ticket was found.
 */
export function revealWaitlistFormForTicket(ticketType, { root = document } = {}) {
  if (!ticketType || !root || typeof root.querySelector !== 'function') {
    return false;
  }
  const sel = (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function')
    ? window.CSS.escape(String(ticketType))
    : String(ticketType).replace(/["\\]/g, '\\$&');
  const button = root.querySelector(`.toggle-waitlist-form[data-ticket="${sel}"]`);
  const form = root.querySelector(`.waitlist-join-form[data-ticket="${sel}"]`);
  if (form && !form.classList.contains('hidden')) {
    // Already expanded — just bring it into view.
    if (typeof form.scrollIntoView === 'function') {
      form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    return true;
  }
  if (button && button.dataset.waitlistToggleInit === '1') {
    // Handler bound (initWaitlistManagement ran on this DOM): trigger the full
    // expand flow (estimates + bot protection + focus trap + scroll).
    button.click();
    return true;
  }
  // Button missing, or present but not yet initialised (e.g. a soft-reloaded DOM
  // where initWaitlistManagement has not re-run) — a programmatic click would be a
  // no-op. Signal failure so the caller hard-reloads the page, which re-renders the
  // sold-out state with bound waitlist handlers.
  return false;
}

export function initWaitlistManagement(root, { openInlineLogin } = {}) {
  if (!root) {
    return;
  }

  // LOW-07: Initialize join-waitlist forms
  initWaitlistJoinForms(root, { openInlineLogin });

  // Existing: Mark active waitlist entries
  root.querySelectorAll('[data-waitlist-state="active"]').forEach((box) => {
    if (box.dataset.waitlistInit === '1') {
      return;
    }
    box.dataset.waitlistInit = '1';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');

    const card = box.closest('[data-registration-status]');
    if (card && !card.dataset.waitlistHighlight) {
      card.dataset.waitlistHighlight = '1';
      card.classList.add('waitlist-highlight');
    }

    const comment = box.querySelector('[data-waitlist-comment]');
    if (comment && !comment.dataset.waitlistCommentInit) {
      comment.dataset.waitlistCommentInit = '1';
      comment.setAttribute('role', 'note');
    }
  });
}
